'use client';

import { useApolloClient } from '@apollo/client/react';
import { DocumentNode, getOperationAST } from 'graphql';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import env, { BE_URL } from 'config/env';
import { AUTH_METHOD, getStoredAuthMethod, getStoredAuthToken } from 'context/Auth/auth-provider.utils';
import { getAccessTokenSilently } from 'context/Auth/msal-utils';
import {
  SELF_STUDY_ASSIGNMENT,
  SELF_STUDY_ASSIGNMENT_POLL,
  SELF_STUDY_ASSIGNMENTS,
} from 'network/graphql/SelfStudyAssignment';
import { API_PREFIX } from 'network/helpers/config';
import { authorizationHeaders } from 'network/helpers/parameterProcessors';
import { SseEnvelope, connectSse } from 'network/sse/sse-client';
import { ddlog } from 'utils/miscellaneous';
import { isCypress } from 'utils/testing';

import { SSEContext } from './sse-connection-context';

export const SSE_EVENTS_PATH = `${API_PREFIX}/me/events`;

// Signal, not data: an event only says what changed, and the authoritative
// state is refetched over GraphQL. Events dropped while disconnected are
// harmless because every (re)connect refetches the lot.
export const QUERIES_BY_EVENT_TYPE: Record<string, DocumentNode[]> = {
  'self-study-assignment.status': [SELF_STUDY_ASSIGNMENT, SELF_STUDY_ASSIGNMENT_POLL, SELF_STUDY_ASSIGNMENTS],
};
const ALL_QUERIES = [...new Set(Object.values(QUERIES_BY_EVENT_TYPE).flat())];

// Silent only: a background reconnect must never send the tab to sign in. With no token
// (expired, or signed out in another tab) there is nothing to authenticate with, so retry quietly.
const NO_SESSION = new Error('sse: no session token');

const getHeaders = async (): Promise<Record<string, string>> => {
  const authMethod = getStoredAuthMethod();
  const token = authMethod === AUTH_METHOD.SCHOOL ? getStoredAuthToken() : await getAccessTokenSilently();
  if (!token) {
    throw NO_SESSION;
  }
  return authorizationHeaders(authMethod, token);
};

// Matches the client's bar for a healthy stream.
const RECOVERED_AFTER_MS = 30_000;

const toError = (error: unknown): Error => (error instanceof Error ? error : new Error(String(error)));

export const SSEProvider = ({ children }: PropsWithChildren) => {
  const apolloClient = useApolloClient();
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    // Mocked and Cypress runs have no backend to stream from; the poll fallback covers them.
    if (env.MOCKED || isCypress() || !BE_URL) {
      return undefined;
    }

    // Only mounted, unskipped queries: a document list would also refetch skipped ones, like
    // the poll once an assignment is ready. A failed refetch surfaces in that query's error state.
    const refetch = (queries: DocumentNode[]) => {
      const names = new Set(queries.flatMap((query) => getOperationAST(query)?.name?.value ?? []));
      void apolloClient
        .refetchQueries({ include: 'active', onQueryUpdated: (query) => names.has(query.queryName ?? '') })
        .catch(() => undefined);
    };

    // One log per outage, not one per backed-off retry: a dead backend would
    // otherwise write to Datadog every half minute from every open tab. The outage
    // is over once a stream stays up, not on every open of one that keeps dropping.
    let outageLogged = false;
    let recovered: ReturnType<typeof setTimeout> | undefined;

    const connection = connectSse({
      url: `${BE_URL}${SSE_EVENTS_PATH}`,
      getHeaders,
      onOpen: () => {
        recovered = setTimeout(() => {
          outageLogged = false;
        }, RECOVERED_AFTER_MS);
        setConnected(true);
        refetch(ALL_QUERIES);
      },
      onClose: () => {
        clearTimeout(recovered);
        setConnected(false);
      },
      onEvent: (envelope: SseEnvelope) => {
        const queries = QUERIES_BY_EVENT_TYPE[envelope.type];
        if (queries) {
          refetch(queries);
        }
      },
      onError: (error) => {
        if (error === NO_SESSION || outageLogged) {
          return;
        }
        outageLogged = true;
        ddlog.error('sse: connection failed', { path: SSE_EVENTS_PATH }, toError(error));
      },
    });

    return () => {
      clearTimeout(recovered);
      connection.close();
      setConnected(false);
    };
  }, [apolloClient]);

  const value = useMemo(() => ({ connected }), [connected]);

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>;
};
